// R36 — THE BINDING PAGES: the Terms, the Privacy Policy and the FAQ.
//
// The content is HERS — owner-authored markdown at the repo root, bundled at build time and rendered
// faithfully. Nothing here rewrites, summarises or fills in anything. What these components decide is
// only HOW it is presented, and two of those decisions have teeth:
//
//   1. THE POPUP AND THE PAGE MUST SHOW THE SAME DOCUMENT. `LegalModal`'s own comment states it —
//      "same markdown, same renderer as the full page, so the popup can never show a different
//      document than /terms does" — and it was held by nothing: the router typed one mapping from
//      page to source and the modal typed a second one, twenty lines apart. T2-2's whole consent law
//      is that the version she agreed to is the version recorded, and she agrees at the POPUP.
//      `LEGAL_DOC_TITLES` is the one place a document's name is written, and App.tsx keeps the one
//      page -> source map beside the raw imports, so there is no second roster to keep in step
//      (R31's shape: derive, so the crossing stops existing).
//
//   2. THE FAQ'S JUMP MENU AND ITS ANCHORS ARE THE SAME LIST, OR EVERY LINK IS DEAD. The menu is
//      built here from the document's own `##` headings; the anchors are written by `Markdown` and
//      ONLY when `headingIds` is passed — an optional prop that GATES a capability (R27's shape).
//      Drop it, or let the two heading parsers drift by one character, and every link in the menu
//      silently does nothing. A tap that does nothing has no error state for a member to report.
//
// They moved out of App.tsx for the reason every roadmap-7 extraction has: props in, tree out, no
// hook, so a gate CALLS them.
//
// **R85 RETIRED THE REASON `LegalModal` STAYED BEHIND, AND THE RETIREMENT IS WRITTEN HERE RATHER
// THAN DELETED (R67's lesson, paid a third time).** R36 left it in App.tsx because it renders
// `ModalBackdrop`, "which holds two effects, and a component out here cannot import back out of
// App.tsx" — **and R81 moved `ModalBackdrop` into `src/components`, so the import goes the ordinary
// way now.** A measured reason is still a claim about the instrument, and an instrument changes.
//
// **THE ONE THING THAT MOVED WITH IT IS THE SOURCE, AS A PROP, AND THAT IS WHAT KEEPS R36's LAW.**
// The `?raw` markdown imports are a build-time feature that must stay in App.tsx (a `.md?raw`
// specifier would take this whole module out of `loadTsx`'s reach, which is the one outcome roadmap
// 7 must never produce), so `LEGAL_DOC_SOURCES` stays there and is read AT THE CALL SITE — exactly
// as `LegalPage` has always read it. One mapping still, at both surfaces, in one spelling.
import { ArrowLeft, X } from "lucide-react";
import { ModalBackdrop } from "./ModalBackdrop";
import { Markdown } from "./Markdown";
import { headingSlug } from "../lib/format";

// The ONE place each legal document's name is written. It used to be typed at the router arm AND
// again inside the modal, which is how a document ends up titled one thing on the page she reads and
// another on the popup she agrees to.
export const LEGAL_DOC_TITLES = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
} as const;

export type LegalDoc = keyof typeof LEGAL_DOC_TITLES;
// K4: the house rules open in the same popup at sign-up. They are not a legal document, so they stay out of the table above.
export const HOUSE_RULES_TITLE = "House rules";
export type ModalDoc = LegalDoc | "rules";

// T3-2 — the FAQ's sections are the document's OWN `##` headings, so adding a section to faq.md adds
// it to the menu with no code change. Pulled out as a pure function because it is one half of a
// crossing: what this returns must match, exactly, the ids `Markdown` writes on the same source.
export function faqSections(source: string) {
  return String(source).replace(/\r\n/g, "\n").split("\n")
    .map((line) => /^##\s+(?!#)(.*)$/.exec(line.trim()))
    .filter(Boolean)
    .map((m) => (m as RegExpExecArray)[1].trim());
}

// The owner filled every placeholder on 2026-07-16 (entity, dates, governing law, venue). What keeps
// them filled is enforcement, not a reminder: the gate fails if a bracketed placeholder reappears.
export function LegalPage({ title, source, goHome }: { title: string; source: string; goHome: () => void }) {
  return (
    <section className="page-shell legal-page">
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        Back
      </button>
      <article className="legal-page__body" aria-label={title}>
        <Markdown source={source} />
      </article>
    </section>
  );
}

// T3-2 — THE FAQ, GROUPED, WITH A JUMP MENU. The menu scrolls rather than filters: a member who came
// looking for one answer keeps everything else in reach beneath it. `headingIds` is what makes the
// anchors it jumps to exist at all, and it is passed HERE rather than defaulted on, because a
// document with no menu has no use for ids.
export function FaqPage({ source, goHome }: { source: string; goHome: () => void }) {
  const sections = faqSections(source);
  return (
    <section className="page-shell legal-page faq-page">
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        Back
      </button>
      {sections.length > 1 ? (
        <nav className="faq-jump" aria-label="Jump to a section">
          {sections.map((s) => (
            <button
              key={s}
              type="button"
              className="faq-jump__link"
              data-jump={headingSlug(s)}
              onClick={() => document.getElementById(headingSlug(s))?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              {s}
            </button>
          ))}
        </nav>
      ) : null}
      <article className="legal-page__body" aria-label="Frequently Asked Questions">
        <Markdown source={source} headingIds />
      </article>
    </section>
  );
}

// T2-2 — THE DOCUMENT SHE IS AGREEING TO, READABLE WITHOUT LEAVING SIGNUP. Same markdown, same
// renderer as the full page, so the popup can never show a different document than /terms does.
// R36: that sentence used to be a promise. The router typed one page -> source mapping and this
// modal typed a second one twenty lines away, and she AGREES at the popup — so a drift here would
// record consent against a document she was never shown. `LEGAL_DOC_SOURCES` in App.tsx is the one
// mapping, and `LEGAL_DOC_TITLES` above is the one set of names.
//
// R85: hookless, so `scripts/r36-legal-pages.mjs` CALLS it and reads the document back out of the
// tree — which is what the popup she agrees at always needed and a regex could never give it.
export function LegalModal({ doc, source, onClose }: { doc: ModalDoc; source: string; onClose: () => void }) {
  const title = doc === "rules" ? HOUSE_RULES_TITLE : LEGAL_DOC_TITLES[doc];
  return (
    <ModalBackdrop label={title} className="legal-modal-backdrop" onClose={onClose}>
      <div className="legal-modal">
        <div className="legal-modal__head">
          <h2>{title}</h2>
          <button type="button" className="legal-modal__close" aria-label="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <article className="legal-modal__body legal-page__body" aria-label={title}>
          <Markdown source={source} />
        </article>
        <button type="button" className="button button--primary legal-modal__done" onClick={onClose}>Done</button>
      </div>
    </ModalBackdrop>
  );
}
